import { initializeCustomer } from '../auth/store/auth.thunk';
import type { AddToCartRequest, BuyItNowRequest } from '../cart/models/cart.model';
import { CartAction } from '../cart/models/cart.model';
import { addToCart, buyItNow } from '../cart/store/cart.thunk';
import type { ButtonService } from '../elements/button.service';
import { loadProduct } from '../elements/store/element.thunk';
import { t } from '../i18n';
import { MembershipConfirmChangeModal } from '../membership/modals/membership-confirm-change.modal';
import { MembershipConfirmTermsModal } from '../membership/modals/membership-confirm-terms.modal';
import type { Membership, Tier } from '../membership/models/membership.model';
import {
  ConfirmTermsResult,
  InteractionType,
  MembershipContractUpdateRequest,
  Term,
  TermProductVariant,
} from '../membership/models/membership.model';
import { addMembershipToCart, updateMembership } from '../membership/store/membership.thunk';
import { getChangeType, isContractFreeTier } from '../membership/utils/membership.utlis';
import { modalService } from '../modal/modal.service';
import type { Product } from '../shopify/models/product.model';
import type { AppDispatch } from '../store';
import { getOrigin, getSearchParams, getWindow, windowNavigate } from '../utils/window.utils';

export class ProductFormService {
  private dispatch: AppDispatch;

  selectors: string[];
  buyItNowSelectors: string[];

  readonly IGNORE_SELECTOR =
    ':not(.single-ignore, .single-login-button, [data-zp-add-to-cart], [data-single-button-processed="true"], [data-single-clone="true"])';

  constructor(
    dispatch: AppDispatch,
    private buttonService: ButtonService
  ) {
    this.dispatch = dispatch;

    this.selectors = [
      '.add-to-cart',
      '.add_to_cart',
      '.add-to-cart-button',
      '.add_to_cart_button',
      '.button-add-to-cart',
      '.button_add_to_cart',
      '.add-to-cart-btn',
      '.add_to_cart_btn',
      '.btn-add-to-cart',
      '.btn_add_to_cart',
      '.addToCart',
      '.AddToCart',
      '.AddtoCart',
      '.addtocart',
      '.btn-addtocart',
      '.btn--sticky_addtocart',
      '.qsc-btn--add-to-cart',
      '#add-to-cart',
      '#add-to-cart-btn',
      '#btn-add-to-cart',
      '#addToCartButton',
      '#addToCartBtn',
      '#btnAddToCart',
      '#AddToCart',
      '#shopify_add_to_cart',
    ].reduce(
      (list, value) => {
        return (
          list.push('button'.concat(value, this.IGNORE_SELECTOR)),
          list.push('input'.concat(value, this.IGNORE_SELECTOR)),
          list.push('a'.concat(value, this.IGNORE_SELECTOR)),
          list
        );
      },
      [
        'form[action^="/cart/add"] button[type=submit]:not(.shopify-payment-button__button):not(.shopify-payment-button__more-options)',
        'form[action^="/cart/add"] input[type=submit]',
        'a[href^="/cart/add"]',
        'form[action="/cart/add"] #button-cart',
        '.product-atc-btn',
        'form[action="/cart/add"] .shg-btn',
        'form[action^="/cart/add"] [data-product-add]',
        '.btn--add-to-cart',
        '.product-form--atc-button',
        '.cart__checkout-btn',
        '.product-atc__button',
        '.complete-design-a',
        '.product_buttonContainer-addToCart a',
        '#mwAddToCart',
        'button[data-zp-link-type="cart"]',
        'button[data-pf-type="ProductATC"]',
        '[data-action="add-to-cart"]',
        '[data-action="AddToCart"]',
        '[name=add]',
        '[name=addToCart]',
        '[name=AddToCart]',
        '.g-stickybar-buynow',
      ]
        .map((selector) => {
          return ''.concat(selector, this.IGNORE_SELECTOR);
        })
        .concat(getWindow().SINGLE_CUSTOM_CART_BUTTON_SELECTORS || [])
    );
    this.buyItNowSelectors = [
      '.shopify-payment-button__button',
      '.shopify-payment-button__more-options',
    ].reduce((list, value) => {
      return (
        list.push('button'.concat(value, this.IGNORE_SELECTOR)),
        list.push('input'.concat(value, this.IGNORE_SELECTOR)),
        list.push('a'.concat(value, this.IGNORE_SELECTOR)),
        list
      );
    }, []);
  }

  copyToDisabledButton(
    formElement: HTMLElement,
    targetSection: HTMLElement | null,
    customLabel: string | null,
    defaultLabel: string
  ): void {
    const element = this.buttonService.getFirstElementWithMultipleSelectors(
      this.selectors,
      formElement
    );
    if (!!element) {
      const clonedButton = element.cloneNode(true) as HTMLInputElement | HTMLButtonElement;
      clonedButton.innerHTML = !!customLabel && customLabel !== '' ? customLabel : defaultLabel;
      clonedButton.disabled = true;
      targetSection?.appendChild(clonedButton);
    }
  }

  addAlreadySubscribedButton(
    targetSection: HTMLElement | null,
    customLabel: string,
    defaultLabel: string
  ): void {
    if (!targetSection) return;

    // Clear existing buttons first
    targetSection.innerHTML = '';

    const alreadySubButton = document.createElement('button');
    alreadySubButton.innerHTML = !!customLabel && customLabel !== '' ? customLabel : defaultLabel;
    alreadySubButton.classList.add('button', 'button--full-width');
    alreadySubButton.addEventListener('click', ($event: PointerEvent) => {
      this.haltEvent($event);
      getWindow().window.location.href = `${getWindow().origin}/apps/single/membership`;
    });
    targetSection.appendChild(alreadySubButton);
  }

  addUpgradeButton(targetSection: HTMLElement | null, tier: Tier, membership: Membership): void {
    if (!targetSection) return;

    // Clear existing buttons first
    targetSection.innerHTML = '';

    const upgradeButton = document.createElement('button');
    upgradeButton.innerHTML = t('membership.manageLabel', {
      label: this.capitalize(membership.accountSettings.membershipLabel),
    });
    upgradeButton.classList.add('button', 'button--full-width');
    upgradeButton.addEventListener('click', ($event: PointerEvent) => {
      this.haltEvent($event);
      this.handleMembershipUpgrade(tier);
    });
    targetSection.appendChild(upgradeButton);
  }

  bindAddToCart(formElement: HTMLElement, upsell: boolean, membership: boolean, tier?: Tier): void {
    if (!upsell && !membership) {
      return;
    }
    const elements = this.buttonService
      .getAllElementsWithMultipleSelectors(this.selectors, formElement)
      .filter(this.notCheckoutForm);
    elements.forEach((element) => {
      if (
        element.getAttribute('data-single-button-processed') === 'true' ||
        element.getAttribute('data-single-clone') === 'true'
      ) {
        return;
      }
      this.buttonService.toggleVisibility(element, false);
      const clonedButton = element.cloneNode(true) as HTMLElement;
      clonedButton.setAttribute(
        'style',
        'display:none!important;visibility:hidden;position:absolute;left:-6666px;'
      );
      clonedButton.setAttribute('data-single-clone', 'true');
      if (membership) {
        const span = document.createElement('span');
        span.textContent = t('perks.join');
        element.innerHTML = '';
        element.appendChild(span);
        if (!element.classList.contains('button--full-width')) {
          element.classList.add('button--full-width');
        }
        if (!!tier?.legalTerms) {
          element.parentElement?.classList.add('has-legal-terms');
          const legalTerms = document.createElement('single-product-form-legal-terms-v2');
          legalTerms.setAttribute('legal-terms', '' + tier.legalTerms);
          // Button Updates
          element.setAttribute('disabled', 'true');
          element.parentElement?.prepend(legalTerms);
        }
        element.parentNode?.insertBefore(clonedButton, element);
      } else {
        element.parentNode?.insertBefore(clonedButton, element);
      }
      element.addEventListener(
        'click',
        ($event) => {
          this.haltEvent($event);
          // Use currentTarget (the button) instead of target (could be the span)
          const clickedButton = $event.currentTarget as HTMLElement;
          const form = clickedButton.closest('form');
          const productVariantId =
            getSearchParams().get('variant') || this.findFormElementByName(form, 'id')?.value;
          const sellingPlanId = this.findFormElementByName(form, 'selling_plan')?.value;
          const quantity = parseInt(this.findFormElementByName(form, 'quantity')?.value, 10) || 1;
          this.dispatch(
            addToCart({
              originalAddToCartButton: element,
              addToCartButton: clonedButton,
              productVariantId,
              upsell,
              membership,
              cartAction: CartAction.NONE,
              sellingPlanId,
              quantity,
            } as AddToCartRequest)
          );
        },
        { capture: true }
      );
      element.setAttribute('data-single-button-processed', 'true');
      this.buttonService.toggleVisibility(element, true);
    });
  }

  bindBuyItNow(formElement: HTMLElement, membership: boolean): void {
    if (!membership) {
      return;
    }
    const elements = this.buttonService
      .getAllElementsWithMultipleSelectors(this.buyItNowSelectors, formElement)
      .filter(this.notCheckoutForm);
    elements.forEach((element) => {
      if (
        element.getAttribute('data-single-button-processed') === 'true' ||
        element.getAttribute('data-single-clone') === 'true'
      ) {
        return;
      }
      if (
        element.getAttribute('aria-hidden') === 'true' ||
        (element as HTMLButtonElement).disabled
      ) {
        setTimeout(() => this.bindBuyItNow(formElement, membership), 100);
        return;
      }
      const paymentButtonDiv = element.closest('[data-shopify="payment-button"]') as HTMLElement;
      this.buttonService.toggleVisibility(paymentButtonDiv, false);
      const clonedButton = element.cloneNode(true) as HTMLElement;
      clonedButton.setAttribute('data-single-clone', 'true');
      paymentButtonDiv.parentNode?.insertBefore(clonedButton, paymentButtonDiv);
      clonedButton.addEventListener(
        'click',
        ($event) => {
          this.haltEvent($event);
          const clickedButton = $event.target as HTMLElement;
          const form = clickedButton.closest('form');
          const productVariantId =
            getSearchParams().get('variant') || this.findFormElementByName(form, 'id')?.value;
          const sellingPlanId = this.findFormElementByName(form, 'selling_plan')?.value;
          const features = JSON.parse(
            this.buttonService.getFirstElement('script[id="shopify-features"]').innerHTML
          );
          this.dispatch(
            buyItNow({
              accessToken: features.accessToken,
              productVariantId,
              sellingPlanId,
            } as BuyItNowRequest)
          );
        },
        { capture: true }
      );
      element.setAttribute('data-single-button-processed', 'true');
    });
  }

  startInitialization(): HTMLElement[] {
    const elements = this.buttonService
      .getAllElementsWithMultipleSelectors(this.selectors)
      .filter(this.notCheckoutForm);
    elements.forEach((element) => this.buttonService.toggleVisibility(element, false));
    this.dispatch(loadProduct());
    this.dispatch(initializeCustomer());
    return elements;
  }

  initializeProductFormForProduct(product: Product, elements: HTMLElement[]): void {
    if (!product) {
      elements.forEach((element) => {
        this.buttonService.toggleVisibility(element, true);
      });
      return;
    }
    const gated = product?.tags?.includes('single-gated');
    const upsell = product?.tags?.includes('single-upsell');
    const membership = product?.tags?.includes('single-membership');
    if (!gated && !upsell && !membership) {
      elements.forEach((element) => {
        this.buttonService.toggleVisibility(element, true);
      });
      return;
    }
    elements.forEach((element) => {
      if (!element.closest('single-product-form-wrapper-v2')) {
        const form = element.closest('form');
        const formWrapper = document.createElement('single-product-form-wrapper-v2');
        formWrapper.setAttribute('product-id', '' + product.id);
        formWrapper.setAttribute('gated-product', '' + gated);
        formWrapper.setAttribute('upsell-product', '' + upsell);
        formWrapper.setAttribute('membership-product', '' + membership);
        form?.appendChild(formWrapper);
      }
      this.buttonService.toggleVisibility(element, true);
    });
  }

  private notCheckoutForm(element: HTMLElement): boolean {
    const enclosingForm = element.closest('form');
    if (!enclosingForm) {
      return true;
    }
    const action = enclosingForm.getAttribute('action');
    if (!action) {
      return true;
    }
    return '/checkout' !== action && '/cart' !== action;
  }

  private handleMembershipUpgrade(tier: Tier): void {
    const url =
      !!tier.postPurchaseUrl && tier.postPurchaseUrl !== ''
        ? tier.postPurchaseUrl
        : `${getOrigin()}/apps/single/membership#/my-account`;
    windowNavigate(url);
  }

  private haltEvent($event: any): void {
    $event.preventDefault();
    $event.stopPropagation();
    $event.stopImmediatePropagation();
  }

  private capitalize(value: string): string {
    if (!value) {
      return '';
    }
    const trimmed = value.trim().toLowerCase();
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  }

  private findFormElementByName(form: HTMLFormElement | null, name: string): HTMLInputElement {
    const element = form?.querySelector('[name="' + name + '"]') as HTMLInputElement;
    if (!element && !!form?.id) {
      return document.querySelector(
        '[form="' + form.id + '"][name="' + name + '"]'
      ) as HTMLInputElement;
    } else {
      return element;
    }
  }
}
